/**
 * AiOperationsPanel — master-detail replacement for the old 10-deep vertical
 * AI Operations stack. A left rail lists each operation; the detail pane shows
 * exactly ONE at a time, so it is never an endless scroll and never blank
 * (each segment renders its own descriptive header even while its panel loads).
 */
import React, { useState } from "react";
import {
  Activity, SlidersHorizontal, Workflow, MessageSquareQuote,
  ListChecks, FileSearch, TrendingUp,
} from "lucide-react";
import ErrorBoundary from "./ErrorBoundary";
import AiFreshnessPanel from "./AiFreshnessPanel";
import ModelSettingsPanel from "./ModelSettingsPanel";
import ProcessModelPanel from "./ProcessModelPanel";
import BoardQAPanel from "./BoardQAPanel";
import RunPlanPanel from "./RunPlanPanel";
import EvidenceReviewPanel from "./EvidenceReviewPanel";
import CsiTailoringPanel from "./CsiTailoringPanel";
import CompareModels from "./CompareModels";

const SEGMENTS = [
  {
    id: "freshness", label: "Artefact freshness", Icon: Activity,
    blurb: "Check whether the stored Process Model & Board Q&A reflect the latest prompts and data — and regenerate before you issue a client pack.",
    render: (runId) => <AiFreshnessPanel runId={runId} />,
  },
  {
    id: "models", label: "Model settings", Icon: SlidersHorizontal,
    blurb: "Choose which AI model powers each generator. Defaults are sensible; cost per run is shown.",
    render: () => <ModelSettingsPanel />,
  },
  {
    id: "process", label: "Target operating process", Icon: Workflow,
    blurb: "A best-in-class, sector/size/standards-tailored future-state process as an editable swim-lane PPTX, with RACI/KPI matrix.",
    render: (runId) => (<><ProcessModelPanel runId={runId} /><CompareModels runId={runId} generator="process" label="Target operating process" /></>),
  },
  {
    id: "boardqa", label: "Board Q&A", Icon: MessageSquareQuote,
    blurb: "The toughest questions a sceptical board is likely to ask — each with a crisp, evidence-anchored answer.",
    render: (runId) => (<><BoardQAPanel runId={runId} /><CompareModels runId={runId} generator="boardqa" label="Board Q&A" /></>),
  },
  {
    id: "plan", label: "Executable plan", Icon: ListChecks,
    blurb: "The delivery plan narrative — phased, owned and sequenced.",
    render: (runId) => (<><RunPlanPanel runId={runId} /><CompareModels runId={runId} generator="plan" label="Executable plan" /></>),
  },
  {
    id: "evidence", label: "Evidence review", Icon: FileSearch,
    blurb: "An AI review reconciling the captured evidence against the scores.",
    render: (runId) => (<><EvidenceReviewPanel runId={runId} /><CompareModels runId={runId} generator="evidence" label="Evidence review" /></>),
  },
  {
    id: "csi", label: "CSI tailoring", Icon: TrendingUp,
    blurb: "Tailors the continual-improvement catalogue to this client.",
    render: (runId) => (<><CsiTailoringPanel runId={runId} /><CompareModels runId={runId} generator="csi" label="CSI tailoring" /></>),
  },
];

export default function AiOperationsPanel({ runId }) {
  const [active, setActive] = useState("freshness");
  const seg = SEGMENTS.find((s) => s.id === active) || SEGMENTS[0];

  return (
    <div className="flex flex-col lg:flex-row border ek-line ek-surface min-h-[420px]" data-testid="ai-operations-panel">
      {/* Master rail */}
      <nav className="lg:w-60 lg:flex-shrink-0 lg:border-r ek-line ek-surface-2 flex lg:flex-col overflow-x-auto lg:overflow-visible">
        <div className="hidden lg:block px-4 pt-4 pb-2 text-[10.5px] font-semibold uppercase tracking-[0.16em] ek-text-muted">AI Operations</div>
        {SEGMENTS.map((s) => {
          const on = s.id === active;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(s.id)}
              data-testid={`ai-nav-${s.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-[12.5px] font-medium whitespace-nowrap border-l-2 transition-colors ${
                on ? "ek-surface text-[#7737F6] border-[#7737F6]" : "ek-text-soft border-transparent hover:bg-zinc-100 hover:text-zinc-900"
              }`}
            >
              <s.Icon size={15} className="shrink-0" /> {s.label}
            </button>
          );
        })}
      </nav>

      {/* Detail pane */}
      <div className="flex-1 p-5 lg:p-6 overflow-x-hidden" data-testid={`ai-detail-${seg.id}`}>
        <div className="flex items-center gap-2 text-[#7737F6] mb-1">
          <seg.Icon size={16} />
          <h4 className="text-[13px] font-semibold uppercase tracking-[0.14em]">{seg.label}</h4>
        </div>
        <p className="text-[12px] ek-text-muted mb-5 max-w-2xl">{seg.blurb}</p>
        <ErrorBoundary label={seg.label}>
          <div className="space-y-4">{seg.render(runId)}</div>
        </ErrorBoundary>
      </div>
    </div>
  );
}
